/** Per-user recent-command list for the Command Palette. Lives in
 *  localStorage so recents survive a page reload — a typical session
 *  is editor-tab-driven (close + reopen), and losing recents on
 *  every reload would defeat the "stuff I do often surfaces first"
 *  affordance.
 *
 *  Storage shape: a JSON array of string ids, capped at MAX_RECENTS.
 *  Forward-compat strategy: a future feature (timestamps, per-document
 *  scoping) can either bump the storage key or upgrade in-place with
 *  a defensive parse — for v1, "just the ids" keeps the migration
 *  story trivial.
 *
 *  Storage failures (private mode, quota exceeded) are silently
 *  swallowed by the underlying namespace helper. A broken recents
 *  list is a degraded experience, never a blocker — every other
 *  palette feature works without it. */

import { createPersistedNamespace, safeStorage } from './user-prefs-storage';

const MAX_RECENTS = 8;

const namespace = createPersistedNamespace<string[]>({
  storageKey: 'image-editor:command-palette:recent',
  defaultValue: [],
  parse: (raw): string[] => {
    if (!Array.isArray(raw)) return [];
    return raw.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENTS);
  },
});

/** Read the persisted recent-command id list. Always returns a
 *  defensive copy; callers can splice freely. Bad / missing data
 *  parses as an empty list (no thrown error). */
export function loadRecentCommands(storage: Storage = safeStorage()): string[] {
  return namespace.load(storage);
}

/** Promote a command id to the head of the recents list and persist
 *  the result. Returns the new list (useful for tests + callers that
 *  want to skip a follow-up `loadRecentCommands` round-trip). */
export function pushRecentCommand(id: string, storage: Storage = safeStorage()): string[] {
  const current = namespace.load(storage);
  const without = current.filter((x) => x !== id);
  const next = [id, ...without].slice(0, MAX_RECENTS);
  namespace.save(next, storage);
  return next;
}

/** Wipe the persisted list. Wired to the "Clear recents" affordance
 *  in a future palette settings menu; for v1 it's only used by
 *  tests + manual cleanup. */
export function clearRecentCommands(storage: Storage = safeStorage()): void {
  namespace.clear(storage);
}

/** Exported for tests that want to spy on cap behavior. */
export const COMMAND_PALETTE_MAX_RECENTS = MAX_RECENTS;
